const parseEnabled = (value, fallback = true) => {
  if (value === undefined) return fallback;
  const normalized = String(value).trim().toLowerCase();
  return !["0", "false", "off", "no"].includes(normalized);
};

const parseVersion = (value) => {
  if (!value) return "2.4.0";
  const v = String(value).trim();
  return /^\d+\.\d+\.\d+$/.test(v) ? v : "2.4.0";
};

export const APP_VERSION = parseVersion(process.env.REACT_APP_VERSION);

export const FEATURE_STATUS = {
  NEW: "new",
  UPDATED: "updated",
  STABLE: "stable",
  BETA: "beta",
  DEPRECATED: "deprecated",
};

export const features = {
  mailEnabled: parseEnabled(process.env.REACT_APP_ENABLE_MAIL, true),
  reportsEnabled: parseEnabled(process.env.REACT_APP_ENABLE_REPORTS, true),
};

export const featureCatalog = [
  {
    id: "tasks",
    label: "Tasks",
    description: "Structured task engine with status progression and approval-aware handling",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.0.0",
    versionLastUpdated: "2.0.0",
    icon: "CheckCircleRoundedIcon",
  },
  {
    id: "intent-guidance",
    label: "Intent Guidance Engine",
    description: "Intent-first routing engine that interprets user goals and navigates to the right workspace context",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "2.0.0",
    versionLastUpdated: "2.2.0",
    icon: "PsychologyRoundedIcon",
  },
  {
    id: "teams",
    label: "Teams",
    description: "Collaborative workspaces with role-based access control",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.0.0",
    versionLastUpdated: "2.0.0",
    icon: "GroupsRoundedIcon",
  },
  {
    id: "conference",
    label: "Conference",
    description: "Built-in team conferencing for live blocker resolution",
    status: FEATURE_STATUS.UPDATED,
    versionIntroduced: "1.2.0",
    versionLastUpdated: "2.1.0",
    icon: "LanguageRoundedIcon",
  },
  {
    id: "calendar",
    label: "Calendar",
    description: "Schedule planning with events, notes, and deadline views",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.0.0",
    versionLastUpdated: "1.5.0",
    icon: "CalendarMonthRoundedIcon",
  },
  {
    id: "insights",
    label: "Insights",
    description: "Personal and team journaling with mood and energy tracking",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.3.0",
    versionLastUpdated: "2.0.0",
    icon: "InsightsRoundedIcon",
  },
  {
    id: "mail",
    label: "Mail",
    description: "Team messaging and inbox for async communication",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.4.0",
    versionLastUpdated: "2.0.0",
    icon: "MailRoundedIcon",
    enabledFlag: "mailEnabled",
  },
  {
    id: "extensions",
    label: "Extensions",
    description: "Deadline extension requests with approval workflows",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.1.0",
    versionLastUpdated: "2.0.0",
    icon: "ExtensionRoundedIcon",
  },
  {
    id: "reports",
    label: "Reports",
    description: "Delivery health and team workload analytics",
    status: FEATURE_STATUS.NEW,
    versionIntroduced: "2.2.0",
    versionLastUpdated: "2.2.0",
    icon: "BarChartRoundedIcon",
    enabledFlag: "reportsEnabled",
  },
  {
    id: "performance-refresh",
    label: "Performance Refresh",
    description: "Faster startup with lighter route bundles, on-demand notifications, and cleaner font loading",
    status: FEATURE_STATUS.NEW,
    versionIntroduced: "2.4.0",
    versionLastUpdated: "2.4.0",
    icon: "BoltRoundedIcon",
  },
  {
    id: "remote-work",
    label: "Remote Work",
    description: "Mode-based employee and manager workspace with guided flows",
    status: FEATURE_STATUS.NEW,
    versionIntroduced: "2.3.0",
    versionLastUpdated: "2.3.0",
    icon: "WorkspacesRoundedIcon",
  },
  {
    id: "attendance",
    label: "Attendance",
    description: "Team punch-in tracking with live presence and time spent",
    status: FEATURE_STATUS.NEW,
    versionIntroduced: "2.3.0",
    versionLastUpdated: "2.3.0",
    icon: "FactCheckRoundedIcon",
  },
  {
    id: "assigned-tasks",
    label: "Assigned Tasks",
    description: "Multi-assigned tasks with cross-team visibility",
    status: FEATURE_STATUS.UPDATED,
    versionIntroduced: "1.5.0",
    versionLastUpdated: "2.2.0",
    icon: "AssignmentIndRoundedIcon",
  },
  {
    id: "plan-my-day",
    label: "Plan My Day",
    description: "Daily planning hub connecting tasks, calendar, and teams",
    status: FEATURE_STATUS.NEW,
    versionIntroduced: "2.1.0",
    versionLastUpdated: "2.2.0",
    icon: "WbSunnyRoundedIcon",
  },
  {
    id: "team-tasks",
    label: "Team Tasks",
    description: "Team-scoped task views with role-aware actions",
    status: FEATURE_STATUS.STABLE,
    versionIntroduced: "1.1.0",
    versionLastUpdated: "2.0.0",
    icon: "GroupWorkRoundedIcon",
  },
  {
    id: "guest-registry",
    label: "Guest Registry",
    description: "Guest preview users are now tracked in a separate MongoDB guest list from registered accounts",
    status: FEATURE_STATUS.NEW,
    versionIntroduced: "2.4.0",
    versionLastUpdated: "2.4.0",
    icon: "BadgeRoundedIcon",
  },
  {
    id: "guest-flows",
    label: "Guest Flows",
    description: "Pre-auth task, insight, mail, and team creation workflows with cleaner guest session tracking",
    status: FEATURE_STATUS.UPDATED,
    versionIntroduced: "1.6.0",
    versionLastUpdated: "2.4.0",
    icon: "PersonOutlineRoundedIcon",
  },
  {
    id: "guided-tour",
    label: "Guided Tour",
    description: "Updated onboarding tour for new users to learn the latest workspace changes faster",
    status: FEATURE_STATUS.UPDATED,
    versionIntroduced: "2.0.0",
    versionLastUpdated: "2.4.0",
    icon: "MapRoundedIcon",
  },
];

export const getNewAndUpdatedFeatures = () =>
  featureCatalog.filter((f) => f.status === FEATURE_STATUS.NEW || f.status === FEATURE_STATUS.UPDATED);

export const isFeatureAvailable = (featureId) => {
  const feature = featureCatalog.find((f) => f.id === featureId);
  if (!feature) return false;
  if (feature.status === FEATURE_STATUS.DEPRECATED) return false;
  if (feature.enabledFlag && !features[feature.enabledFlag]) return false;
  return true;
};

export const getFeatureStatus = (featureId) => {
  const feature = featureCatalog.find((f) => f.id === featureId);
  return feature?.status || null;
};

export const getFeaturePrompts = (includeNew = true) => {
  const available = featureCatalog
    .filter((f) => isFeatureAvailable(f.id))
    .filter((f) => f.status !== FEATURE_STATUS.DEPRECATED);
  if (!includeNew) return available.filter((f) => f.status === FEATURE_STATUS.STABLE || f.status === FEATURE_STATUS.BETA);
  return available;
};
